import { Injectable } from '@angular/core';
import {
    CoreMissionRoles,
    FlightConformanceResultDto,
    FlyFreelyError,
    GqlMissionCriteria,
    MissionRoleDto,
    MissionService,
    MissionSortFieldName,
    MissionSortFields,
    MissionSummary,
    MissionSummaryDto,
    RequestersMissionApprovalDto,
    SimplePersonDto,
    validatedSortEntryArray,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import moment, { Moment } from 'moment';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type MissionFilterOptions =
    | 'UPCOMING'
    | 'ACTIVATED'
    | 'INACTIVE_APPROVALS'
    | 'CONFORMANCE_BREACHES';

export interface UpcomingMissionData {
    id: number;
    name: string;
    rpic: SimplePersonDto;
    missionDate: string;
    timeZone: string;
    convertedMissionDate: Moment;
    locationName: string;
    rpaNicknames: string;
    workflowName: string;
}

interface UpcomingMissionSearchCriteria {
    page: number;
    limit: number;
    sorting: ColumnSortPreferences;
    organisationId: number;
    authorityIds?: number[];
    personId: number;
    filter: MissionFilterOptions;
}

@Injectable()
export class UpcomingMissionsDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private missionsSource = new ReplaySubject<UpcomingMissionData[]>(1);
    private currentPageSource = new BehaviorSubject<number>(0);
    private totalItemsSource = new ReplaySubject<number>(1);

    private searchCriteriaSubject =
        new ReplaySubject<UpcomingMissionSearchCriteria>(1);

    searchCriteria$ = this.searchCriteriaSubject.asObservable();

    working$ = this.workingSource.asObservable();
    missions$ = this.missionsSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(private missionService: MissionService) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.searchCriteriaSubject.subscribe(criteria =>
            this.updateMissionTable(criteria)
        );
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.missionsSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.searchCriteriaSubject.complete();
    }

    findMissions(
        page: number,
        limit: number,
        sorting: ColumnSortPreferences,
        organisationId: number,
        personId: number,
        authorityIds?: number[],
        filter: MissionFilterOptions = 'UPCOMING'
    ) {
        this.searchCriteriaSubject.next({
            page,
            limit,
            sorting,
            organisationId,
            authorityIds,
            personId,
            filter
        });
    }

    private buildCriteriaForFilter(
        filter: MissionFilterOptions,
        organisationId: number,
        authorityIds: number[] | undefined,
        personId: number
    ): GqlMissionCriteria {
        const base = {
            organisationId,
            authorityIds,
            orOrganisationId: null as null,
            crewId: personId,
            isDummy: false,
            outstandingApprovals: false,
            readyForFinalisation: false,
            crewCoreRoleIn: [
                CoreMissionRoles.PILOT_IN_COMMAND,
                CoreMissionRoles.PILOT,
                CoreMissionRoles.NONE
            ]
        };

        switch (filter) {
            case 'UPCOMING':
                return {
                    ...base,
                    status: [
                        MissionSummaryDto.Status.DRAFT,
                        MissionSummaryDto.Status.READY_TO_FLY
                    ],
                    // Only missions from today onwards
                    startTime: moment().startOf('day').toISOString()
                };

            case 'ACTIVATED':
                return {
                    ...base,
                    status: [
                        MissionSummaryDto.Status.ON_SITE,
                        MissionSummaryDto.Status.FLYING,
                        MissionSummaryDto.Status.READY_TO_FLY
                    ]
                };

            case 'INACTIVE_APPROVALS':
                return {
                    ...base,
                    status: [MissionSummaryDto.Status.DRAFT],
                    missionApprovalStatus: [
                        RequestersMissionApprovalDto.Status.REJECTED,
                        RequestersMissionApprovalDto.Status.REVOKED
                    ],
                    outstandingApprovals: false,
                    startTime: moment().startOf('day').toISOString()
                };

            case 'CONFORMANCE_BREACHES':
                return {
                    ...base,
                    conformanceResultStatus: [
                        FlightConformanceResultDto.Status.SENT,
                        FlightConformanceResultDto.Status.PRELIMINARY
                    ]
                };
        }
    }

    private updateMissionTable({
        page,
        limit,
        sorting,
        organisationId,
        authorityIds,
        personId,
        filter
    }: UpcomingMissionSearchCriteria) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();

        const criteria = this.buildCriteriaForFilter(
            filter,
            organisationId,
            authorityIds,
            personId
        );

        // this.subscriptionTypes$ is used here to ensure it has a value and prevent a race condition.
        const sortFields = validatedSortEntryArray(
            this.parseFilterFieldNames(
                sorting?.column ?? null
            ) as MissionSortFieldName,
            sorting?.ascending,
            MissionSortFields
        );

        this.missionService
            .findMissionSummaries(criteria, {
                page: page,
                pageSize: limit,
                sortFields
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions => {
                    const findRpic = (mission: MissionSummary) => {
                        return mission.missionCrew.find(
                            p =>
                                p.role.coreRole ===
                                MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                        )?.person;
                    };
                    const findRpaNames = (mission: MissionSummary) => {
                        const names = mission.rpaLinkList
                            ?.map((c, i) =>
                                i < 3 ? c.rpa.nickname : i === 3 ? '...' : null
                            )
                            .filter(c => c != null);
                        return names.join(', ');
                    };
                    const missionDetails: UpcomingMissionData[] =
                        missions.results.map((r, i) => ({
                            id: r.id,
                            name: r.name,
                            rpic: findRpic(r),
                            missionDate: r.missionDate,
                            timeZone: r.timeZone,
                            convertedMissionDate: moment(r.missionDate).tz(
                                r.timeZone
                            ),
                            locationName: r.location?.name,
                            rpaNicknames: findRpaNames(r),
                            workflowName: r.workflowVersion?.workflow?.name
                        }));
                    this.totalItemsSource.next(missions.count);
                    this.currentPageSource.next(page);
                    this.missionsSource.next(missionDetails);
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    console.error(error);
                    this.missionsSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                    doneWorking();
                }
            });
    }

    parseFilterFieldNames(name: string) {
        switch (name) {
            case 'missionCrewNames':
                return 'missionCrew';

            case 'missionType.name':
                return 'missionOperationType';

            case 'convertedMissionDate':
                return 'missionDate';

            default:
                return name;
        }
    }
}
